/**
 * Emisión multicanal — el modal sin equivalente en ningún proyecto previo.
 *
 * **Un acto administrativo, N consultas.** El analista marca los canales por
 * los que quiere preguntar y se abre una consulta por cada uno; la primera
 * respuesta concluyente cierra el requerimiento y abandona las demás (`NEG-8`).
 *
 * Dos decisiones de esta pantalla merecen quedar escritas:
 *
 *  · **El botón dice cuántas consultas abre.** «Emitir 2 consultas», no
 *    «Emitir». La acción tiene consecuencias plurales y el texto las nombra.
 *
 *  · **Un canal no habilitado se ve, se explica y ofrece la salida**, en lugar
 *    de desaparecer sin motivo. Que falte no es un error del usuario: es
 *    configuración de la entidad que alguien puede arreglar.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Textarea,
} from '@mpfn/ui'
import { Callout, DatePicker, FormField } from '@mpfn/patterns'
import { useEmitirRequerimiento, useEntidades, usePolitica } from '../api/consultas.js'
import { useCatalogos } from '../api/catalogos.js'
import type { Canal, DocumentoDeclarado } from '../api/tipos.js'
import { irA } from '../navegacion/Enlace.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { diasHabiles, hoy } from '../lib/formato.js'

interface Props {
  documento: DocumentoDeclarado
  abierto: boolean
  onCambio: (abierto: boolean) => void
}

export function DialogoEmision({ documento, abierto, onCambio }: Props) {
  const catalogos = useCatalogos()
  const politica = usePolitica()
  const entidades = useEntidades()
  const emitir = useEmitirRequerimiento()

  const [elegidos, setElegidos] = useState<ReadonlyArray<{ canal: Canal; canalEntidadId?: string }>>([])
  const [fechaEmision, setFechaEmision] = useState(hoy())
  const [motivo, setMotivo] = useState('')
  const [destinatarioNombre, setDestinatarioNombre] = useState('')
  const [destinatarioCargo, setDestinatarioCargo] = useState('')

  const entidad = (entidades.data ?? []).find((item) => item.id === documento.entidadId)
  const esReiteracion = documento.ordinalSiguiente > 1

  // A quién va el oficio: se propone el representante del catálogo cada vez
  // que se abre. Lo que se corrija aquí va solo en este oficio.
  useEffect(() => {
    if (!abierto) return
    setDestinatarioNombre(entidad?.representanteNombre ?? '')
    setDestinatarioCargo(entidad?.representanteCargo ?? '')
  }, [abierto, entidad])

  /**
   * Un canal ofrecible tiene que estar habilitado en la política Y tener
   * destino configurado en la entidad. Se listan los cuatro igualmente: los
   * que no se pueden usar se muestran con el motivo.
   */
  const opciones = useMemo(() => {
    const habilitadosEnPolitica = new Set(politica.data?.canalesHabilitados ?? [])
    return catalogos.canales.map((definicion) => {
      const canalEntidad = entidad?.canales.find(
        (item) => item.canal === definicion.codigo && item.desactivadoEn === undefined,
      )
      const oficial = entidad?.canales.find(
        (item) =>
          item.canal === definicion.codigo && item.esOficial && item.desactivadoEn === undefined,
      )
      const elegido = oficial ?? canalEntidad
      const enPolitica = habilitadosEnPolitica.size === 0 || habilitadosEnPolitica.has(definicion.codigo)

      const plazo =
        elegido?.plazoDias ??
        politica.data?.plazosPorCanal[definicion.codigo] ??
        definicion.plazoPorDefectoDias

      return {
        definicion,
        canalEntidadId: elegido?.id,
        destino: elegido?.destino,
        plazo,
        disponible: Boolean(elegido) && enPolitica,
        motivoNoDisponible: !enPolitica
          ? 'Deshabilitado en la política vigente'
          : 'No configurado para esta entidad',
      }
    })
  }, [catalogos.canales, entidad, politica.data])

  /** Sale oficio si alguno de los canales elegidos lo envía: la consulta web, no. */
  const llevaOficio = elegidos.some(
    (item) => opciones.find((opcion) => opcion.definicion.codigo === item.canal)?.definicion.generaDocumento,
  )

  function alternar(canal: Canal, canalEntidadId: string | undefined, marcado: boolean): void {
    setElegidos((previo) =>
      marcado
        ? [...previo, { canal, ...(canalEntidadId ? { canalEntidadId } : {}) }]
        : previo.filter((item) => item.canal !== canal),
    )
  }

  return (
    <Dialog
      open={abierto}
      onOpenChange={(valor) => {
        if (!valor) {
          setElegidos([])
          setMotivo('')
        }
        onCambio(valor)
      }}
    >
      <DialogContent>
        <DialogTitle>
          {esReiteracion
            ? `Emitir requerimiento ${String(documento.ordinalSiguiente)} · reiteración`
            : 'Emitir requerimiento'}
        </DialogTitle>
        <DialogDescription>
          {documento.denominacion}
          {entidad ? ` · ${entidad.nombre}` : ''}
        </DialogDescription>

        {!documento.puedeEmitir && (
          <Callout tone="warning" title="Este documento todavía no se puede requerir">
            {documento.entidadId === undefined
              ? 'Falta resolver la entidad emisora en el catálogo.'
              : 'Hay un requerimiento vivo, o el documento está descartado o derivado.'}
          </Callout>
        )}

        <fieldset className="sigfisc-canales">
          <legend>Canales de consulta · al menos uno</legend>
          {opciones.map((opcion) => {
            const marcado = elegidos.some((item) => item.canal === opcion.definicion.codigo)
            return (
              <div
                key={opcion.definicion.codigo}
                className="sigfisc-canales__opcion"
                data-disponible={opcion.disponible ? 'si' : 'no'}
              >
                <Checkbox
                  id={`canal-${opcion.definicion.codigo}`}
                  checked={marcado}
                  disabled={!opcion.disponible || !documento.puedeEmitir}
                  onCheckedChange={(valor) => {
                    alternar(opcion.definicion.codigo, opcion.canalEntidadId, valor === true)
                  }}
                />
                <label htmlFor={`canal-${opcion.definicion.codigo}`}>
                  <span className="sigfisc-canales__etiqueta">{opcion.definicion.etiqueta}</span>
                  <span className="sigfisc-canales__destino">
                    {opcion.disponible ? opcion.destino : opcion.motivoNoDisponible}
                  </span>
                  <span className="sigfisc-canales__meta">
                    {opcion.plazo === 0 ? 'Respuesta inmediata' : diasHabiles(opcion.plazo)}
                    {opcion.definicion.generaDocumento ? ' · genera oficio' : ' · sin oficio'}
                  </span>
                </label>
                {!opcion.disponible && (
                  <Button
                    variant="link"
                    size="sm"
                    type="button"
                    onClick={() => {
                      irA('entidades')
                    }}
                  >
                    Configurar entidad
                  </Button>
                )}
              </div>
            )
          })}
        </fieldset>

        {llevaOficio && (
          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField
              id="destinatario-nombre"
              label="Destinatario del oficio"
              hint={
                entidad?.representanteNombre
                  ? 'Del catálogo de entidades. Lo que corrija aquí va solo en este oficio.'
                  : 'El catálogo no tiene el representante de esta entidad: escríbalo, o el oficio irá a la entidad.'
              }
            >
              <Input
                value={destinatarioNombre}
                onChange={(evento) => {
                  setDestinatarioNombre(evento.target.value)
                }}
              />
            </FormField>
            <FormField id="destinatario-cargo" label="Cargo del destinatario">
              <Input
                value={destinatarioCargo}
                onChange={(evento) => {
                  setDestinatarioCargo(evento.target.value)
                }}
              />
            </FormField>
          </div>
        )}

        <FormField id="fecha-emision" label="Fecha de emisión">
          <DatePicker
            id="fecha-emision"
            value={fechaEmision}
            onChange={setFechaEmision}
            yearRange={{ from: Number(hoy().slice(0, 4)) - 1, to: Number(hoy().slice(0, 4)) }}
          />
        </FormField>

        {esReiteracion && (
          <FormField
            id="motivo-emision"
            label="Motivo de la reiteración"
            hint="Queda en la auditoría y ayuda a explicar por qué se insistió."
          >
            <Textarea
              rows={2}
              value={motivo}
              onChange={(evento) => {
                setMotivo(evento.target.value)
              }}
            />
          </FormField>
        )}

        <div className="sigfisc-dialogo__acciones">
          <DialogClose asChild>
            <Button variant="secondary" type="button">
              Cancelar
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            type="button"
            loading={emitir.isPending}
            disabled={elegidos.length === 0 || !documento.puedeEmitir}
            onClick={() => {
              emitir.mutate(
                {
                  documentoId: documento.id,
                  canales: elegidos,
                  fechaEmision,
                  ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
                  ...(llevaOficio
                    ? { destinatario: { nombre: destinatarioNombre.trim(), cargo: destinatarioCargo.trim() } }
                    : {}),
                },
                {
                  onSuccess: (datos) => {
                    avisarExito(
                      `Requerimiento ${String(datos.requerimiento.ordinal)} emitido`,
                      datos.requerimiento.oficio.lleva
                        ? `Se abrieron ${plural(datos.consultasAbiertas, 'consulta', 'consultas')}. Descargue el oficio para numerarlo y firmarlo.`
                        : `Se abrieron ${plural(datos.consultasAbiertas, 'consulta', 'consultas')}.`,
                    )
                    setElegidos([])
                    setMotivo('')
                    onCambio(false)
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo emitir el requerimiento')
                  },
                },
              )
            }}
          >
            {elegidos.length === 0
              ? 'Elija al menos un canal'
              : `Emitir ${plural(elegidos.length, 'consulta', 'consultas')}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
